import PageHero from "../components/site/PageHero";
import CTABanner from "../components/site/CTABanner";
import ServiceBody from "../components/site/ServiceBody";
import FAQBlock from "../components/site/FAQBlock";
import ProcessSteps from "../components/site/ProcessSteps";
import useSEO from "../hooks/useSEO";
import {
  Radio,
  Video,
  ShieldAlert,
  MapPin,
  Users,
  Bell,
  Building2,
  ActivitySquare,
  Zap,
} from "lucide-react";

const IMG = "https://customer-assets-m6fa6gv7.emergentagent.net/job_fire-cctv-access/artifacts/4694gjo3_image.png";

export default function Monitoring() {
  useSEO({
    title: "Alarm & CCTV Monitoring Ireland | Remote Verification & Personal Alarms | FSS",
    description:
      "Professional monitoring, alarm response, remote CCTV verification, personal panic alarms and lone worker protection — coordinated by FSS across Ireland.",
    canonicalPath: "/monitoring",
  });

  return (
    <>
      <PageHero
        eyebrow="Monitoring & Personal Protection"
        title="Professional monitoring and personal protection."
        subtitle="Monitoring and alert solutions designed to help identify, verify and escalate security events — for intruder alarms, CCTV systems, personal alarms, panic alarms and system fault notifications."
        image={IMG}
        imageAlt="Live CCTV camera feeds viewed remotely on a smartphone"
        ctaPrimary={{ to: "/contact", label: "Discuss Monitoring Options" }}
        ctaSecondary={{ to: "/contact", label: "Request a Monitoring Assessment" }}
      />

      <ServiceBody
        includedSubtitle="Monitoring & personal protection services"
        included={[
          { icon: Radio, title: "Alarm Monitoring", copy: "Intruder alarm activation alerts, event verification, keyholder notifications and escalation to an agreed response plan." },
          { icon: Video, title: "Live CCTV Monitoring", copy: "Alarm-triggered or event-triggered CCTV verification with visual confirmation and agreed escalation procedures." },
          { icon: ActivitySquare, title: "Remote CCTV Verification", copy: "Analytic or alarm-triggered alerts are reviewed and assessed, and the agreed response plan is followed." },
          { icon: ShieldAlert, title: "Personal Panic Alarms", copy: "Portable and fixed panic buttons, mobile alert devices and contact escalation." },
          { icon: MapPin, title: "Lone Worker Protection", copy: "Panic activation, missed check-ins, location information (where offered) and manager notifications." },
          { icon: Users, title: "Vulnerable Person Alarms", copy: "Where offered, respectful alarm solutions supporting older or vulnerable users." },
          { icon: Bell, title: "System Fault Monitoring", copy: "Alerts for communication, power, battery, camera-offline and recorder faults." },
          { icon: Building2, title: "Multi-Site Monitoring", copy: "Central visibility, site-by-site records, coordinated response plans and scalable support." },
        ]}
        whoFor={[
          "Businesses with monitored intruder alarms",
          "Retail, warehouse and commercial premises with CCTV",
          "Organisations with lone workers or after-hours activity",
          "Managed estates and property portfolios",
          "Sites where alarm verification helps reduce false callouts",
        ]}
        benefits={[
          "Structured event handling in line with an agreed response plan",
          "Verification where the service supports it",
          "Reduced reliance on any single individual",
          "Clear incident records and escalation history",
          "Coordinated multi-site coverage",
        ]}
        defaultService="Alarm Monitoring"
      />

      {/* Verification section */}
      <section id="verification" className="fss-section bg-slate-50 border-y border-slate-200">
        <div className="fss-container grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="fss-overline">Remote CCTV Verification</div>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-semibold text-slate-900 leading-tight">
              How verification typically works
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-4 text-slate-600 leading-relaxed">
            <ol className="space-y-3">
              {[
                "A system or analytic alert is triggered.",
                "The monitoring team reviews the available information.",
                "The event is assessed against the agreed plan.",
                "The agreed response plan is followed.",
                "Relevant contacts are notified.",
                "An incident record may be created.",
              ].map((s, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="w-6 h-6 flex items-center justify-center rounded-sm bg-green-700 text-white text-xs font-bold shrink-0">{i + 1}</span>
                  <span>{s}</span>
                </li>
              ))}
            </ol>
            <div className="mt-4 p-4 border border-slate-200 bg-white rounded-sm text-xs text-slate-600">
              We do not state that emergency services are automatically
              contacted — the specific response plan depends on your
              arrangements with the monitoring provider.
            </div>
          </div>
        </div>
      </section>

      {/* Self vs professional */}
      <section className="fss-section bg-white">
        <div className="fss-container">
          <div className="max-w-2xl mb-10">
            <div className="fss-overline">Comparison</div>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-semibold text-slate-900 leading-tight">
              Self-monitoring vs professional monitoring
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="fss-card">
              <Zap className="w-8 h-8 text-red-600" strokeWidth={1.75} />
              <h3 className="mt-4 font-display text-xl font-semibold text-slate-900">Self-monitoring</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">The system sends alerts directly to the owner, manager or nominated user. The user must notice the alert, assess the event and decide what action to take.</p>
              <p className="mt-3 text-xs text-slate-500">Availability depends on the nominated person.</p>
            </div>
            <div className="fss-card">
              <Radio className="w-8 h-8 text-green-700" strokeWidth={1.75} />
              <h3 className="mt-4 font-display text-xl font-semibold text-slate-900">Professional monitoring</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">A professional monitoring service receives and manages relevant alerts in line with an agreed response plan — including structured event handling and clear incident records.</p>
              <p className="mt-3 text-xs text-slate-500">Verification depends on the service selected.</p>
            </div>
          </div>
        </div>
      </section>

      <FAQBlock
        eyebrow="Monitoring FAQ"
        title="Buyer questions"
        items={[
          { q: "What happens when an alarm activates?", a: "The activation is received by the monitoring service and handled in line with the agreed response plan — including verification (where offered), keyholder notification and any further escalation." },
          { q: "What is professional alarm monitoring?", a: "A service that receives, verifies (where offered) and escalates alarm events in line with your response plan, rather than relying on the alarm being noticed by one nominated person." },
          { q: "What is live CCTV monitoring?", a: "Video-based monitoring that may be continuous, event-triggered, alarm-triggered or scheduled — depending on the service. We will clarify how the service you are considering operates." },
          { q: "Is CCTV watched continuously?", a: "Not necessarily. Many CCTV monitoring services operate on event or alarm triggers rather than constant live viewing. We recommend confirming exactly how your service operates." },
          { q: "What is remote verification?", a: "A monitoring team reviews the available information following an alert, assesses the event and follows the agreed response plan." },
          { q: "How can monitoring reduce false callouts?", a: "Verified events help focus response on genuine incidents, which can reduce unnecessary callouts and keyholder disruption." },
          { q: "Who is contacted during an incident?", a: "The keyholders and responders defined in your response plan. Contact with emergency services depends on the specific arrangements in place." },
          { q: "Can monitoring work with an existing alarm?", a: "In many cases yes — subject to the panel and communication path." },
          { q: "Can monitoring work with existing CCTV?", a: "Often yes — depending on the CCTV system and network configuration." },
          { q: "Can alerts be sent to a mobile app?", a: "Yes — most modern systems support mobile notifications alongside professional monitoring." },
          { q: "What is the difference between a personal alarm and a panic alarm?", a: "Personal alarms are typically portable devices used by individuals; panic alarms are fixed or portable buttons used to trigger an immediate alert." },
          { q: "Does FSS provide lone worker protection?", a: "Where confirmed, lone worker devices support panic activation, missed check-in escalation and manager notifications." },
          { q: "Can multiple sites be monitored?", a: "Yes — multi-site monitoring provides central visibility, coordinated response plans and site-by-site records." },
          { q: "What affects monitoring cost?", a: "Number of alarms and cameras, response requirements, verification level, keyholder arrangements, number of sites and any additional device provision." },
          { q: "Are monitoring plans available?", a: "Yes — we can discuss the options and confirm what suits your premises." },
        ]}
        bg="slate"
      />

      <CTABanner
        title="Protection beyond the premises."
        subtitle="Contact FSS to discuss alarm monitoring, CCTV verification, panic alarms or multi-site monitoring."
        primary={{ to: "/contact", label: "Discuss Monitoring Options" }}
        secondary={{ to: "/contact", label: "Request a Monitoring Assessment" }}
      />
    </>
  );
}
